import React, { useState, useMemo, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import Tabs from '@/components/Tabs'
import { AuthType } from '@/components/Tabs/index.type'
import CustomTextInput from '@/components/CustomTextInput'
import CustomButton from '@/components/CustomButton'
import CountryCodeSelector from '@/components/CountryCodeSelector'
// import OtherLoginItems from '@/components/OtherLoginItems'
import { Toast, Checkbox } from 'react-vant';
import { useTranslation } from 'react-i18next'
import { useSendCode } from '@/hooks/useSendCode'
import { APIWeb } from '@/api'
import { APP_NAME } from '@/config/constants'

export default React.memo(function Register() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [selectedType, setSelectedType] = useState<AuthType>(AuthType.Email)
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [inviteCode, setInviteCode] = useState('')
  const [countryCode, setCountryCode] = useState('+86')
  const [errors, setErrors] = useState<{ email?: string; inviteCode?: string; phone?: string }>({})
  const [isAgreed, setIsAgreed] = useState(false)
  const { sendCode, sendCodeLoading } = useSendCode()

  const handleCountryCodeChange = useCallback((code: string) => {
    if (code !== '+86') {
      return Toast.info(t('暂不支持该国家地区手机号注册'))
    }
    setCountryCode(code)
  }, [t])
  // 验证邮箱格式
  const validateEmail = useCallback((emailValue: string) => {
    if (!emailValue) {
      return t('请输入邮箱地址')
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(emailValue)) {
      return t('请输入有效的邮箱地址')
    }
    return ''
  }, [t])

  // 验证邀请码格式（必填，但不限制格式）
  const validateInviteCode = useCallback((code: string) => {
    if (!code || code.trim() === '') {
      return t('请输入邀请码')
    }
    return ''
  }, [t])

  // 验证手机号格式（6-15位数字）
  const validatePhone = useCallback((phoneValue: string) => {
    if (!phoneValue) {
      return t('请输入手机号')
    }
    const phoneRegex = /^\d{6,15}$/
    if (!phoneRegex.test(phoneValue)) {
      return t('请输入有效的手机号6至15位数字')
    }
    return ''
  }, [t])

  // 处理邮箱输入变化
  const handleEmailChange = useCallback((value: string) => {
    setEmail(value)
    const error = validateEmail(value)
    setErrors(prev => ({ ...prev, email: error }))
  }, [validateEmail])

  // 处理邀请码输入变化
  const handleInviteCodeChange = useCallback((value: string) => {
    setInviteCode(value)
    const error = validateInviteCode(value)
    setErrors(prev => ({ ...prev, inviteCode: error }))
  }, [validateInviteCode])
  // 处理手机号输入变化
  const handlePhoneChange = useCallback((value: string) => {
    setPhone(value)
    const error = validatePhone(value)
    setErrors(prev => ({ ...prev, phone: error }))
  }, [validatePhone])

  // 切换登录注册类型
  const onChange = useCallback((type: AuthType) => {
    setSelectedType(type)
  }, [])

  // 检查表单是否有效
  const isFormValid = useMemo(() => {
    if (selectedType === AuthType.Email) {
      return email && inviteCode && !validateEmail(email) && !validateInviteCode(inviteCode)
    }
    if (selectedType === AuthType.Phone) {
      return phone && inviteCode && !validatePhone(phone) && !validateInviteCode(inviteCode)
    }
    return false
  }, [selectedType, email, phone, inviteCode, validateEmail, validatePhone, validateInviteCode])

  // 处理注册提交
  const handleSendCode = useCallback(async () => {
    if (selectedType === AuthType.Email) {
      const emailError = validateEmail(email)
      const inviteCodeError = validateInviteCode(inviteCode)

      if (emailError || inviteCodeError) {
        setErrors({ email: emailError, inviteCode: inviteCodeError })
        return
      }
      
      // 提交前检查账号是否已注册
      try {
        Toast.loading({message: t('验证中'),forbidClick: true})
        const response = await APIWeb.checkAccountStatus({ account: email })
        Toast.clear()
        if (response?.registered) {
          Toast.info(t('该邮箱已被注册'))
          return
        }
      } catch (error) {
        console.error('检测账号状态失败:', error)
        // 检测失败时允许继续发送验证码
      }
      
      // 提交前检查邀请码是否有效
      try {
        Toast.loading({message: t('验证中'),forbidClick: true})
        const result = await APIWeb.checkInviteCode({ invite_code: inviteCode.trim() })
        if(!result.valid) {
          Toast.fail(t('邀请码无效'))
          return
        }
        Toast.clear()
      } catch (error) {
        console.error('检测邀请码失败:', error)
        return
      }
      
      const data = await sendCode({ account: email, scene: 'register' })
      if (data) {
        navigate(`/register/verification-code`, {
          state: { ...data, type: 'email', inviteCode: inviteCode.trim() }
        })
      }
    } else if (selectedType === AuthType.Phone) {
      const phoneError = validatePhone(phone)
      const inviteCodeError = validateInviteCode(inviteCode)

      if (phoneError || inviteCodeError) {
        setErrors({ phone: phoneError, inviteCode: inviteCodeError })
        return
      }
      
      // 提交前检查账号是否已注册
      try {
        Toast.loading({message: t('验证中'),forbidClick: true})
        const response = await APIWeb.checkAccountStatus({ account: phone })
        Toast.clear()
        if (response?.registered) {
          Toast.info(t('该手机号已被注册'))
          return
        }
      } catch (error) {
        console.error('检测账号状态失败:', error)
        // 检测失败时允许继续发送验证码
      }
      
      // 提交前检查邀请码是否有效
      try {
        Toast.loading({message: t('验证中'),forbidClick: true})
        const result = await APIWeb.checkInviteCode({ invite_code: inviteCode.trim() })
        Toast.clear()

      } catch (error) {
        console.error('检测邀请码失败:', error)
        return
      }
      
      const data = await sendCode({ account: phone, scene: 'register' })
      if (data) {
        navigate(`/register/verification-code`, {
          state: { ...data, type: 'phone', inviteCode: inviteCode.trim(), countryCode }
        })
        setPhone('')
        setInviteCode('')
      }
    }
  }, [navigate, selectedType, email, inviteCode, phone, countryCode, validateEmail, validateInviteCode, validatePhone, sendCode, t])

  const EmailRegisterView = useMemo(
    () => (
      <div className="space-y-4">
        <div className="space-y-2">
          <div className="text-sm text-[#1A1A1A]/60 mb-1">{t('您的电子邮箱')}</div>
          <CustomTextInput
            isErrored={Boolean(errors.email)}
            value={email}
            onChangeText={handleEmailChange}
            placeholder={t('请输入您的电子邮箱')}
            inputMode='email'
          />
          {errors.email && (
            <p className="mt-1 text-xs text-red-600">{errors.email}</p>
          )}
        </div>
        <div className="space-y-2">
          <div className="text-sm text-[#1A1A1A]/60 mb-1">{t('邀请码')}</div>
          <CustomTextInput
            isErrored={Boolean(errors.inviteCode)}
            value={inviteCode}
            onChangeText={handleInviteCodeChange}
            placeholder={t('请输入邀请码')}
          />
          {errors.inviteCode && (
            <p className="mt-1 text-xs text-red-600">{errors.inviteCode}</p>
          )}
        </div>
      </div>
    ),
    [email, inviteCode, handleEmailChange, handleInviteCodeChange, errors.email, errors.inviteCode, t]
  )

  const PhoneRegisterView = useMemo(
    () => (
      <div className="space-y-4">
        <div className="space-y-2">
          <div className="text-sm text-[#1A1A1A]/60 mb-1">{t('您的手机号')}</div>
          <div className={`flex items-center h-12 rounded-2.5 border border-solid bg-white ${errors.phone ? 'border-red-500' : 'border-[#EAECF0]'}`}>
            <div className="h-full flex items-center pl-4 pr-2 [&>div]:border-none [&>div]:h-auto [&>div]:p-0 [&>div]:rounded-none cursor-pointer">
              <CountryCodeSelector
                value={countryCode}
                onChange={handleCountryCodeChange}
              />
            </div>
            <input
              type="tel"
              value={phone}
              onChange={(e) => handlePhoneChange(e.target.value)}
              placeholder={t('请输入您的手机号')}
              inputMode="numeric"
              className="flex-1 h-full px-3 outline-none text-[#1D2129] placeholder-[#C9CDD4] bg-transparent"
            />
          </div>
          {errors.phone && (
            <p className="mt-1 text-xs text-red-600">{errors.phone}</p>
          )}
        </div>
        <div className="space-y-2">
          <div className="text-sm text-[#1A1A1A]/60 mb-1">{t('邀请码')}</div>
          <CustomTextInput
            isErrored={Boolean(errors.inviteCode)}
            value={inviteCode}
            onChangeText={handleInviteCodeChange}
            placeholder={t('请输入邀请码')}
          />
          {errors.inviteCode && (
            <p className="mt-1 text-xs text-red-600">{errors.inviteCode}</p>
          )}
        </div>
      </div>
    ),
    [phone, inviteCode, countryCode, handleCountryCodeChange, handlePhoneChange, handleInviteCodeChange, errors.phone, errors.inviteCode, t]
  )

  const RegisterViewRender = useMemo(
    () => (
      selectedType === AuthType.Email ? EmailRegisterView : PhoneRegisterView
    ),
    [selectedType, EmailRegisterView, PhoneRegisterView]
  )

  return (
    <div className="bg-white h-full">
      <div className="overflow-y-auto">
        <div>
          {/* Logo */}

          <div className="w-full px-6 text-center text-[#1A1A1A] flex flex-col items-center justify-center pt-14 pb-8 text-2xl">
            <img className='w-24 mb-5' src="/icon/logo.svg" alt="logo" />
            {t('创建{{name}}账户', { name: APP_NAME })}
          </div>

          <div className="px-6">
            <Tabs
              items={[
                { type: AuthType.Email, text: t('邮箱注册') },
                { type: AuthType.Phone, text: t('手机注册') }
              ]}
              defaultSelected={AuthType.Email}
              onChange={onChange}
            />
          </div>

          <div className="px-6 mt-8">
            {RegisterViewRender}
          </div>

          <div className="px-6 mt-5 flex items-center">
            <div className='flex-shrink-0 mr-2'>
              <Checkbox
                shape='square'
                checkedColor={"var(--primary-color)"}
                checked={isAgreed}
                onChange={() => setIsAgreed(prev => !prev)}
              />
            </div>
            <span className="text-sm text-gray-600">{t('我同意')}</span>
            <Link to="/privacy-policy" className="text-sm text-[var(--primary-color)]">
              {t('隐私政策')}
            </Link>
          </div>

          <div className="px-6 mt-8">
            <CustomButton
              onClick={handleSendCode}
              loading={sendCodeLoading}
              disabled={!isAgreed || sendCodeLoading || !isFormValid}
            >
              {t('注册')}
            </CustomButton>
          </div>

          <div className="px-6 mt-6 flex items-center justify-center space-x-2">
            <span className="text-sm text-gray-600">{t('已有账户')}</span>
            <Link to="/login" className="text-sm text-[var(--primary-color)]">
              {t('登录')}
            </Link>
          </div>
        </div>
        {/* Other register options */}
        {/* <div className="px-6 mt-12 pb-8">
          <OtherLoginItems />
        </div> */}
      </div>
    </div>
  )
})